// Dine-in table ordering is gated solely by each restaurant's own dine_in_enabled
// column (the real source of truth for "does THIS restaurant have this on").
// There is deliberately no platform-wide switch here anymore — a global kill-switch
// on top of the per-restaurant flag meant flipping it back on for one restaurant
// (e.g. a hotel room-service pilot) would silently activate it for every OTHER
// restaurant that happens to already have dine_in_enabled=true in the DB, which is
// exactly the kind of unintended activation this file exists to prevent.
//
// Persists the dine-in table (from a table QR's ?table= param) per restaurant,
// so it survives navigation from menu → cart without the query string.
const key = (restaurantId: string) => `dine_in_table_${restaurantId}`;
// A restaurant can mix tables and rooms (a hotel's own restaurant, say), so the label alone
// isn't enough to know which word/emoji this specific one gets. The table's own `kind` rides
// along in the QR code's URL (?kind=, set from restaurant_tables.kind when the admin
// generates it — see AdminPage's QR export) and is cached here next to the label. A QR
// printed before this existed simply carries no kind, and callers fall back to the
// restaurant-wide isHotel() guess below, same as it always worked.
const kindKey = (restaurantId: string) => `dine_in_kind_${restaurantId}`;

export function getDineInTable(restaurantId?: string | null): string | null {
  if (!restaurantId) return null;
  try { return localStorage.getItem(key(restaurantId)); } catch { return null; }
}

export function setDineInTable(restaurantId: string, label: string) {
  try { localStorage.setItem(key(restaurantId), label); } catch {}
}

export function getDineInKind(restaurantId?: string | null): 'table' | 'room' | null {
  if (!restaurantId) return null;
  try {
    const v = localStorage.getItem(kindKey(restaurantId));
    return v === 'table' || v === 'room' ? v : null;
  } catch { return null; }
}

export function setDineInKind(restaurantId: string, kind: 'table' | 'room' | null) {
  try {
    if (kind) localStorage.setItem(kindKey(restaurantId), kind);
    else localStorage.removeItem(kindKey(restaurantId));
  } catch {}
}

export function clearDineInTable(restaurantId: string) {
  try { localStorage.removeItem(key(restaurantId)); localStorage.removeItem(kindKey(restaurantId)); } catch {}
}

// A dine-in order (table_label set, no courier) is either a restaurant table order
// or a hotel room-service order — same 3-status flow either way (see getStatusLabels
// in AdminPage.tsx/OrdersPage.tsx, deliberately untouched by this distinction), but
// the two need different wording/emoji ("Tavolinë" vs "Dhomë") everywhere they're
// displayed. Derived purely from the parent restaurant's business_type — no separate
// per-order column needed since a restaurant is either a hotel or it isn't.
export function isHotel(restaurant?: { business_type?: string | null } | null): boolean {
  return restaurant?.business_type === 'hotel';
}

// `kind`, when known (the current table's own snapshot — see getDineInKind above, or an
// order's table_kind), always wins over the restaurant-wide guess.
export function dineInEmoji(restaurant?: { business_type?: string | null } | null, kind?: 'table' | 'room' | null): string {
  return (kind || (isHotel(restaurant) ? 'room' : 'table')) === 'room' ? '🛏️' : '🍽️';
}
